import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import ExportDataModal from '../../components/admin/ExportDataModal';

const AdminUserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [impersonating, setImpersonating] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });
  const [deleting, setDeleting] = useState(false);
  const [exportModal, setExportModal] = useState({ isOpen: false, user: null });
  
  const { user: currentUser } = useAuth();
  
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);
  
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users', {
        params: { page, search: searchTerm }
      });
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const impersonateUser = async (userId) => {
    setImpersonating(userId);
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      
      // Store original token
      localStorage.setItem('originalToken', localStorage.getItem('accessToken'));
      localStorage.setItem('impersonationToken', response.data.token);
      localStorage.setItem('accessToken', response.data.token);
      
      // Redirect to dashboard as impersonated user
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      setImpersonating(null);
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteModal({
      isOpen: true,
      user: user
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.user) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteModal.user.id}`);
      
      // Refresh the user list
      fetchUsers();
      
      // Close modal
      setDeleteModal({ isOpen: false, user: null });
      
      // Show success message (you might want to add a toast notification here)
      console.log('User deleted successfully');
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, user: null });
  };
  
  const handleExportClick = (user) => {
    setExportModal({
      isOpen: true,
      user: user
    });
  };
  
  const handleExportClose = () => {
    setExportModal({ isOpen: false, user: null });
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };
  
  if (loading) {
    return (
      <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-8 text-center">
        <div className="w-8 h-8 mx-auto mb-4">Loading...</div>
        <p className="text-mono text-gray-400 dark:text-[#999999]">Loading users...</p>
      </div>
    );
  }
  
  const canDeleteUser = (user) => {
    // Super admins can delete anyone except themselves
    if (currentUser.role === 'super_admin' && user.id !== currentUser.id) {
      return true;
    }
    // Regular admins can delete non-admin users
    if (currentUser.role === 'admin' && user.role !== 'admin' && user.role !== 'super_admin') {
      return true;
    }
    return false;
  };
  
  return (
    <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg">
      {/* Search Bar */}
      <div className="p-6 border-b border-white/10">
        <input
          type="text"
          placeholder="Search users by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-96 border border-gray-300 dark:border-[#2e2e2e] bg-white dark:bg-[#1f1f1f] rounded px-4 py-2 text-mono"
        />
      </div>
      
      {/* Users Table */}
      <div className="overflow-x-auto">
        <div className="overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className={`border-b border-white/10 bg-gray-100 dark:bg-[#262626]`}>
                <th className="px-6 py-4 text-left text-mono text-xs uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-center text-mono text-xs uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-center text-mono text-xs uppercase tracking-wider">Templates</th>
                <th className="px-6 py-4 text-center text-mono text-xs uppercase tracking-wider">Searches</th>
                <th className="px-6 py-4 text-center text-mono text-xs uppercase tracking-wider">Created</th>
                <th className="px-6 py-4 text-center text-mono text-xs uppercase tracking-wider">Last Active</th>
                <th className="px-6 py-4 text-center text-mono text-xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className={`text-mono font-medium text-gray-800 dark:text-[#f5f5f5]`}>{user.email}</div>
                      {(user.first_name || user.last_name) && (
                        <div className={`text-mono text-xs text-gray-600 dark:text-[#c4c4c4]`}>
                          {user.first_name} {user.last_name}
                        </div>
                      )}
                      {user.invite_code_used && (
                        <div className={`text-mono text-xs text-gray-600 dark:text-[#c4c4c4]`}>
                          Code: {user.invite_code_used}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs text-mono ${
                      user.role === 'admin' || user.role === 'super_admin'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-gray-500/20 text-gray-400 dark:text-[#999999]'
                    }`}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td className={`px-6 py-4 text-center text-mono text-gray-700 dark:text-[#c4c4c4]`}>
                    {user.template_count || 0}
                  </td>
                  <td className={`px-6 py-4 text-center text-mono text-gray-700 dark:text-[#c4c4c4]`}>
                    {user.search_count || 0}
                  </td>
                  <td className={`px-6 py-4 text-center text-mono text-sm text-gray-700 dark:text-[#c4c4c4]`}>
                    {formatDate(user.created_at)}
                  </td>
                  <td className={`px-6 py-4 text-center text-mono text-sm text-gray-700 dark:text-[#c4c4c4]`}>
                    {formatDate(user.last_login)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {user.role !== 'admin' && user.role !== 'super_admin' && (
                        <>
                          <button
                            onClick={() => handleExportClick(user)}
                            className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] text-xs px-3 py-1"
                            title="Export Data"
                          >
                            Export
                          </button>
                          <button
                            onClick={() => impersonateUser(user.id)}
                            disabled={impersonating === user.id}
                            className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] text-xs px-3 py-1 disabled:opacity-50"
                            title="View as User"
                          >
                            {impersonating === user.id ? 'Loading...' : 'View as'}
                          </button>
                        </>
                      )}
                      {canDeleteUser(user) && (
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="text-xs px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 dark:text-red-300 rounded-lg transition-colors"
                          title="Delete User"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className="p-8 text-center">
            <p className={`text-mono text-gray-600 dark:text-[#c4c4c4]`}>
              No users found
            </p>
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 p-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] px-4 py-2 text-mono disabled:opacity-50"
          >
            Previous
          </button>
          <span className={`px-4 py-2 text-mono text-gray-600 dark:text-[#c4c4c4]`}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] px-4 py-2 text-mono disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && deleteModal.user && (
        <ConfirmationModal
          isOpen={deleteModal.isOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          title="Delete User Permanently"
          message={
            <div>
              <p className="mb-4">
                Are you sure you want to permanently delete user <strong>{deleteModal.user.email}</strong>?
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-red-400 text-sm mb-2">⚠️ This action cannot be undone!</p>
                <p className="text-sm text-gray-300">The following will be permanently deleted:</p>
                <ul className="text-sm text-gray-400 dark:text-[#999999] mt-2 list-disc list-inside">
                  <li>User account and profile</li>
                  <li>All query templates ({deleteModal.user.template_count || 0} templates)</li>
                  <li>All search executions ({deleteModal.user.search_count || 0} searches)</li>
                  <li>All search results and analytics data</li>
                  <li>All user settings and preferences</li>
                </ul>
              </div>
            </div>
          }
          confirmText={deleting ? 'Deleting...' : 'Delete Permanently'}
          cancelText="Cancel"
          confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
          isLoading={deleting}
        />
      )}

      {/* Export Data Modal */}
      {exportModal.isOpen && exportModal.user && (
        <ExportDataModal
          isOpen={exportModal.isOpen}
          onClose={handleExportClose}
          user={exportModal.user}
        />
      )}
    </div>
  );
};

export default AdminUserList;